import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Download, Share, Plus, X, MoreVertical, Compass, Copy, Chrome } from "lucide-react";
import { Button } from "@/components/ui/button";

const ua = () => (typeof window !== "undefined" ? window.navigator.userAgent || "" : "");
const isIos = () => /iphone|ipad|ipod/i.test(ua());
const isInAppBrowser = () =>
  /instagram|fbav|fban|fb_iab|line|whatsapp|tiktok|musical_ly/i.test(ua());
const isStandalone = () =>
  typeof window !== "undefined" &&
  (window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone);

export const InstallAppButton = ({ className = "" }) => {
  const [deferred, setDeferred] = useState(null);
  const [modal, setModal] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isStandalone()) { setInstalled(true); return; }
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setModal(false); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice;
      setDeferred(null);
    } else {
      setModal(true);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin + "/installa");
      toast.success("Link copiato! Incollalo in Safari o Chrome.");
    } catch {
      toast.error("Copia manualmente il link dalla barra degli indirizzi.");
    }
  };

  const inApp = isInAppBrowser();
  const ios = isIos();

  return (
    <>
      <Button
        type="button"
        onClick={handleClick}
        data-testid="install-app-btn"
        variant="outline"
        className={`rounded-full h-11 px-5 border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 font-semibold transition-colors duration-300 ${className}`}
      >
        <Download className="w-4 h-4 mr-2" /> Installa l'app
      </Button>

      <AnimatePresence>
        {modal && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-4"
            onClick={() => setModal(false)}
            data-testid="install-instructions"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-sm rounded-2xl bg-zinc-950 ring-1 ring-white/10 p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-2xl">Installa sul telefono</h3>
                <button onClick={() => setModal(false)} data-testid="install-close" className="text-muted-foreground hover:text-white"><X className="w-5 h-5" /></button>
              </div>

              {inApp ? (
                <div data-testid="inapp-warning" className="space-y-4">
                  <div className="rounded-xl bg-amber-500/10 ring-1 ring-amber-500/30 p-4 text-sm text-amber-200">
                    Stai usando il browser interno di Instagram: non permette l'installazione.
                    Apri prima la pagina nel browser del telefono.
                  </div>
                  <ol className="space-y-3 text-sm text-muted-foreground">
                    <li className="flex items-center gap-3"><MoreVertical className="w-5 h-5 text-primary shrink-0" /> Tocca il menu <span className="text-white">⋯</span> in alto a destra.</li>
                    <li className="flex items-center gap-3"><Compass className="w-5 h-5 text-primary shrink-0" /> Scegli <span className="text-white">"Apri nel browser"</span> (Safari o Chrome).</li>
                    <li className="flex items-center gap-3"><Download className="w-5 h-5 text-primary shrink-0" /> Poi tocca di nuovo <span className="text-white">"Installa l'app"</span>.</li>
                  </ol>
                  <Button data-testid="copy-install-link" onClick={copyLink} className="w-full rounded-full bg-primary hover:bg-amber-500 text-primary-foreground font-semibold h-11">
                    <Copy className="w-4 h-4 mr-2" /> Copia il link
                  </Button>
                </div>
              ) : ios ? (
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-center gap-3"><Share className="w-5 h-5 text-primary shrink-0" /> Tocca il pulsante <span className="text-white">Condividi</span> in Safari (in basso).</li>
                  <li className="flex items-center gap-3"><Plus className="w-5 h-5 text-primary shrink-0" /> Scegli <span className="text-white">"Aggiungi a Home"</span>.</li>
                  <li className="flex items-center gap-3"><Download className="w-5 h-5 text-primary shrink-0" /> Conferma: l'app MI.RO' apparirà tra le tue app.</li>
                </ol>
              ) : (
                <ol className="space-y-3 text-sm text-muted-foreground">
                  <li className="flex items-center gap-3"><MoreVertical className="w-5 h-5 text-primary shrink-0" /> Apri il menu del browser <span className="text-white">⋮</span> (Chrome).</li>
                  <li className="flex items-center gap-3"><Download className="w-5 h-5 text-primary shrink-0" /> Scegli <span className="text-white">"Installa app"</span> o "Aggiungi a schermata Home".</li>
                  <li className="flex items-center gap-3"><Chrome className="w-5 h-5 text-primary shrink-0" /> Conferma l'installazione.</li>
                </ol>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
